import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, PROFILE } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (href === "#portfolio") {
      navigate("/portfolio");
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(href), 450);
    } else {
      scrollToSection(href);
    }
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled ? "bg-parchment/85 backdrop-blur-xl border-b border-charcoal-900/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <a
          href="#home"
          data-testid="nav-brand-link"
          onClick={(e) => go(e, "#home")}
          className="flex flex-col leading-tight"
        >
          <span className="font-serif text-lg font-semibold tracking-tight text-charcoal-900">
            {PROFILE.name}
          </span>
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-charcoal-500">
            Sr. Manager — Creative Ops
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={(e) => go(e, link.href)}
              className="text-sm font-medium text-charcoal-600 hover:text-copper-600 transition-colors duration-200"
            >
              {link.label}
            </a>
          ))}
          <button
            data-testid="nav-connect-button"
            onClick={(e) => go(e, "#contact")}
            className="group inline-flex items-center gap-1.5 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-5 py-2.5 hover:bg-copper-600 transition-colors duration-300"
          >
            Let's Connect
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 text-charcoal-900"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-parchment/95 backdrop-blur-xl border-b border-charcoal-900/10"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                  onClick={(e) => go(e, link.href)}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="py-3 text-lg font-serif text-charcoal-900 border-b border-charcoal-900/5"
                >
                  {link.label}
                </motion.a>
              ))}
              <button
                data-testid="nav-mobile-connect-button"
                onClick={(e) => go(e, "#contact")}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-charcoal-900 text-parchment text-sm font-medium px-5 py-3"
              >
                Let's Connect <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
